import { cosemApi } from '@services/providers/cosem';
import { SocialBenefitListItem } from '@services/member/member/member.types';
import { SocialBenefit, SocialBenefitToCreate, SocialBenefitToUpdate } from './social-benefits.types';
import { ApiPaginationResponse, ApiResponse } from '@services/providers/cosem.types';
import {
    SocialBenefit as MemberSocialBenefit
} from '@services/member/member/member.types';
import { ActiveFilter } from '@utils/filters';

const socialBenefitsApi = cosemApi.injectEndpoints({
    endpoints: (build) => ({
        getSocialBenefits: build.mutation<ApiResponse<SocialBenefitListItem[]>, void>({
            query: () => {
                return {
                    url: `/member/social-benefits`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<SocialBenefitListItem[]>) => response
        }),
        getPaginatedSocialBenefits: build.mutation<
            ApiPaginationResponse<SocialBenefit>,
            { limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/social-benefits/paginated`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<SocialBenefit>) => response
        }),
        getArchivedPaginatedSocialBenefits: build.mutation<
            ApiPaginationResponse<SocialBenefit>,
            { limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/social-benefits/archived/paginated`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<SocialBenefit>) => response
        }),
        updateSocialBenefit: build.mutation<ApiResponse<SocialBenefit>, SocialBenefitToUpdate>({
            query: (socialBenefit: SocialBenefitToUpdate) => {
                return {
                    url: `/member/social-benefits/${socialBenefit.id}`,
                    method: 'PUT',
                    body: socialBenefit
                };
            },
            transformResponse: (response: ApiResponse<SocialBenefit>) => response
        }),
        createSocialBenefit: build.mutation<ApiResponse<SocialBenefit>, SocialBenefitToCreate>({
            query: (socialBenefit: SocialBenefitToCreate) => {
                return {
                    url: `/member/social-benefits`,
                    method: 'POST',
                    body: socialBenefit
                };
            },
            transformResponse: (response: ApiResponse<SocialBenefit>) => response
        }),
        deleteSocialBenefit: build.mutation<ApiResponse<SocialBenefit>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/member/social-benefits/${id}`,
                    method: 'DELETE'
                };
            },
            transformResponse: (response: ApiResponse<SocialBenefit>) => response
        }),
        toggleArchiveSocialBenefit: build.mutation<ApiResponse<SocialBenefit>, { id: string|number, archived: boolean }>({
            query: ({ id, archived }) => {
                return { 
                    url: `/member/social-benefits/${id}/archive`, 
                    method: 'PUT',
                    body: { archived }
                };
            },
            transformResponse: (response: ApiResponse<SocialBenefit>) => response
        }),
        getSocialBenefitsEdition: build.mutation<
        ApiPaginationResponse<MemberSocialBenefit>,
        { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/social-benefits/edition`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<MemberSocialBenefit>) => response
        }),
        getSocialBenefitsExport: build.mutation<ApiResponse<string>, {search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ search, orderBy, filters }) => {
                return {
                    url: `/member/social-benefits/edition/export`,
                    method: 'GET',
                    params: { search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiResponse<string>) => response
        }),
    })
});

export const {
    useGetSocialBenefitsMutation,
    useGetPaginatedSocialBenefitsMutation,
    useGetArchivedPaginatedSocialBenefitsMutation,
    useUpdateSocialBenefitMutation,
    useCreateSocialBenefitMutation,
    useDeleteSocialBenefitMutation,
    useToggleArchiveSocialBenefitMutation,
    useGetSocialBenefitsEditionMutation,
    useGetSocialBenefitsExportMutation
} = socialBenefitsApi;
